import React, { FC } from "react";
import { Progress, Table, Tooltip } from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { TooltipComponent } from "../../../../components/Component";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../../utils/skeleton-templates/table";
import { StatusPill } from "./statusMeta";

// Ported from allProjectsListView — same columns, same Redux column-config
// slice (AllProjectsListViewTableColumnsConfig), premium status pill.
export const buildProjectColumns = (onOpen: (record: any) => void): any[] => [
  {
    title: "Project Name",
    dataIndex: "projectTitle",
    key: "projectTitle",
    width: 280,
    fixed: "left",
    render(text: any, record: any) {
      return (
        <span
          className="inlineTableCellLink"
          id={`draftStatus-${record?.projectId}`}
          onClick={() => onOpen(record)}
        >
          {text}
        </span>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "projectStatus",
    key: "projectStatus",
    width: 160,
    render: (text: any) => <StatusPill status={text} />,
  },
  {
    title: "Phases",
    dataIndex: "phases",
    key: "phases",
    width: 200,
    render: (text: any, record: any) => {
      return (
        <Tooltip
          title={`Closed: ${record.completedPhases}, Open: ${record.totalPhases}`}
          placement="top"
        >
          <Progress
            strokeLinecap="butt"
            size={[186, 10]}
            percent={record.phasesCompletionPercentage}
            status="active"
            strokeColor={{ from: "#52c41a", to: "#52c41a" }}
          />
        </Tooltip>
      );
    },
  },
  {
    title: "Issues",
    dataIndex: "issues",
    key: "issues",
    width: 200,
    render: (text: any, record: any) => {
      const { completedIssues, totalIssues } = record || {};
      const percent = Number(
        ((completedIssues / (totalIssues || 1)) * 100).toFixed(2)
      );
      return (
        <Tooltip
          title={`Closed: ${completedIssues}, Open: ${totalIssues}`}
          placement="top"
        >
          <Progress
            strokeLinecap="butt"
            size={[186, 10]}
            percent={percent}
            status="active"
            strokeColor={{ from: "#ff4d4f", to: "#faad14" }}
          />
        </Tooltip>
      );
    },
  },
  {
    title: "Planned Start Date",
    dataIndex: "plannedStartDate",
    key: "plannedStartDate",
    width: 200,
    render: (text: any) => dayjs(text).format("DD-MMM-YYYY"),
  },
  {
    title: "Planned End Date",
    dataIndex: "plannedEndDate",
    key: "plannedEndDate",
    width: 200,
    render: (text: any) => dayjs(text).format("DD-MMM-YYYY"),
  },
  {
    title: "Group",
    dataIndex: "projectGroupName",
    key: "group",
    width: 200,
  },
  {
    title: "Created By",
    dataIndex: "createdByUserName",
    key: "createdByUserName",
    width: 250,
    render: (text: string, record: any, index: any) => {
      return (
        <>
          <span id={`createdBy-tooltip-${index}`}>
            {text?.length > 25 ? `${text?.substring(0, 22)}... ` : text}
          </span>
          {text?.length > 25 && (
            <TooltipComponent
              iconClass="info-circle text-info"
              icon="info"
              id={`createdBy-tooltip-${index}`}
              direction="top"
              text={text}
              containerClassName={""}
            />
          )}
        </>
      );
    },
  },
  {
    title: "Created Date",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 200,
    render: (text: any) => text && dayjs(text).format("DD-MMM-YYYY"),
  },
  {
    title: "Owner",
    dataIndex: "projectOwnerName",
    key: "resourceHeads",
    width: 250,
    render: (text: string, record: any, index: any) => {
      return (
        <>
          <span id={`ownerName-tooltip-${index}`}>
            {text?.length > 25 ? `${text?.substring(0, 22)}... ` : text}
          </span>
          {text?.length > 25 && (
            <TooltipComponent
              iconClass="info-circle text-info"
              icon="info"
              id={`ownerName-tooltip-${index}`}
              direction="top"
              text={text}
              containerClassName={""}
            />
          )}
        </>
      );
    },
  },
  {
    title: "Site",
    dataIndex: "siteName",
    key: "siteName",
    width: 250,
  },
  {
    title: "Budget",
    dataIndex: "budgetName",
    key: "budgetName",
    width: 250,
  },
  {
    title: "Calendar",
    dataIndex: "calendarTitle",
    key: "calendarTitle",
    width: 280,
  },
  {
    title: "RateCard Title",
    dataIndex: "rateCardTitle",
    key: "rateCardTitle",
    width: 280,
  },
];

interface ProjectsTableProps {
  dataSource: any[];
  loading: boolean;
  pagination: any;
  onOpen: (record: any) => void;
  tableOnChange: (pagination: any, filters: any, sorter: any) => void;
}

const ProjectsTable: FC<ProjectsTableProps> = ({
  dataSource,
  loading,
  pagination,
  onOpen,
  tableOnChange,
}) => {
  const { data: columnsConfigData } = useSelector(
    (state: any) => state.AllProjectsListViewTableColumnsConfig
  );

  const columns = buildProjectColumns(onOpen).map((column: any) => ({
    ...column,
    hidden: columnsConfigData?.[column.key] === false,
  }));

  return (
    <div className="pms-hub-table pms-view-fade">
      <SkeletonTable
        loading={loading}
        columns={columns as SkeletonTableColumnsType[]}
      >
        <Table
          rowKey="uid"
          columns={columns}
          dataSource={dataSource}
          pagination={{ ...pagination, size: "small" }}
          onChange={tableOnChange}
          bordered
          size="middle"
          scroll={{ x: 1100 }}
        />
      </SkeletonTable>
    </div>
  );
};

export default ProjectsTable;
